import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Icon } from '#pplx/frontend/aether/components/Icon/index.js';
import { useLinkComponent } from '#pplx/frontend/aether/components/link-component/useLinkComponent.js';
import { useTabsContext } from '#pplx/frontend/aether/components/Tabs/TabsContext.js';
import { Tooltip } from '#pplx/frontend/aether/components/Tooltip/index.js';
import { useRadixProps } from '#pplx/frontend/aether/internal/useRadixProps.js';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cva } from 'class-variance-authority';
import classNames from 'classnames';
import { useRef } from 'react';
// We use aria-selected instead of data-[state=active/inactive] for styling
// because Radix Tooltip's asChild overwrites data-state on the trigger button,
// whereas aria-selected is owned exclusively by Radix Tabs.
const getTabsTabClasses = cva(classNames(
// Reset
'reset', 
// Base typography
'font-sans font-medium select-none', 
// Transition
'transition-colors duration-300', 
// Relative positioning for indicator
'relative', 
// Layout - always flex for consistent structure
'flex gap-1.5 items-center', 
// Typography
'text-sm text-primary', 
// Avoid wrapping for better readability and consistent height, especially with icons
'whitespace-nowrap'), {
    variants: {
        isIconOnly: {
            false: classNames(
            // Text color - inactive
            'aria-[selected=false]:opacity-80', 
            // Text color - full foreground for active
            'aria-selected:opacity-100'),
            true: classNames(
            // Icon color - quiet for inactive
            'aria-[selected=false]:opacity-80', 
            // Icon color - accent for active
            'aria-selected:opacity-100'),
        },
        disabled: {
            false: 'cursor-pointer',
            true: 'cursor-not-allowed opacity-70',
        },
        fullWidth: {
            false: '',
            // Full width layout - equal width distribution and centered
            true: 'flex-1 justify-center',
        },
        size: {
            default: 'py-3.5',
            compact: 'py-2',
        },
        variant: {
            // Underline tabs get accent focus ring
            underline: 'interactable',
            button: 'aria-selected:bg-soft aria-selected:text-primary',
            'button-inverse': 'aria-selected:bg-inverse aria-selected:text-inverse',
        },
    },
    compoundVariants: [
        {
            variant: ['button', 'button-inverse'],
            class: `interactable-alt rounded-full aria-[selected=false]:text-primary aria-[selected=false]:border aria-[selected=false]:border-solid aria-[selected=false]:border-base aria-selected:border aria-selected:border-transparent`,
        },
        // Hover state - only apply when not disabled and inactive (underline)
        {
            isIconOnly: false,
            disabled: false,
            variant: 'underline',
            class: 'aria-[selected=false]:hover:opacity-100',
        },
        {
            isIconOnly: true,
            class: 'px-3',
        },
        // Button unselected hover: match secondary variant Button hover (bg-subtle)
        {
            variant: ['button', 'button-inverse'],
            disabled: false,
            class: 'aria-[selected=false]:hover:bg-subtle',
        },
        // Compact button tabs: smaller height, padding, and text
        {
            variant: ['button', 'button-inverse'],
            size: 'compact',
            class: 'h-6 px-2 text-xs',
        },
        {
            variant: ['button', 'button-inverse'],
            size: 'default',
            class: 'px-3 h-8 text-sm',
        },
        // Disabled tabs must show reduced opacity regardless of selected state.
        // Stacked modifiers (disabled: + aria-) give specificity (0,3,0),
        // beating the isIconOnly variant's aria-only opacity at (0,2,0).
        {
            disabled: true,
            class: 'disabled:aria-selected:opacity-70 disabled:aria-[selected=false]:opacity-70',
        },
    ],
    defaultVariants: {
        isIconOnly: false,
        disabled: false,
        fullWidth: false,
        size: 'default',
        variant: 'underline',
    },
});
export function TabsTab(props) {
    const { value, disabled = false, onClick, ...rest } = props;
    const __radixProps = useRadixProps(rest);
    const { fullWidth, size, variant } = useTabsContext();
    const triggerRef = useRef(null);
    const LinkComponent = useLinkComponent();
    // Type guard to determine variant
    const isIconOnly = 'icon' in props;
    // Extract content based on variant
    const icon = isIconOnly ? props.icon : props.leadingIcon;
    const children = isIconOnly ? undefined : props.children;
    const ariaLabel = isIconOnly ? props['aria-label'] : undefined;
    const href = isIconOnly ? undefined : props.href;
    const tabClassName = classNames(
    // Add 'group' class so children can reference parent state
    'group', getTabsTabClasses({
        isIconOnly,
        disabled,
        fullWidth,
        size: (variant === 'button' || variant === 'button-inverse') && size !== 'compact'
            ? undefined
            : size,
        variant,
    }));
    // When href is provided, render as a link using asChild composition
    const tabTrigger = href ? (_jsx(TabsPrimitive.Trigger, { value: value, disabled: disabled, onClick: onClick, asChild: true, ...__radixProps, children: _jsxs(LinkComponent, { ref: triggerRef, href: href, className: tabClassName, children: [icon && _jsx(Icon, { icon: icon, size: "medium" }), children] }) })) : (_jsxs(TabsPrimitive.Trigger, { ref: triggerRef, value: value, disabled: disabled, onClick: onClick, "aria-label": isIconOnly ? ariaLabel : undefined, className: tabClassName, ...__radixProps, children: [icon && _jsx(Icon, { icon: icon, size: "medium" }), children] }));
    // Wrap in tooltip for icon-only variant.
    // No wrapper element needed — the indicator uses ref-based lookup and all
    // styling uses aria-selected (not data-state), so Tooltip's asChild
    // composition is safe even though it overwrites data-state.
    if (isIconOnly && ariaLabel) {
        return (_jsx(Tooltip, { content: ariaLabel, side: "bottom", disabled: disabled, children: tabTrigger }));
    }
    return tabTrigger;
}
//# sourceMappingURL=TabsTab.js.map